//ReviewCard.tsx
import React from 'react';
import { useNavigate } from "react-router-dom";
import { Box, Card, Chip, Typography, Button, Tooltip, useMediaQuery } from '@mui/material';
import NotificationImportantRoundedIcon from '@mui/icons-material/NotificationImportantRounded';
import { ReviewManagementType } from './ReviewManagementType';
import dayjs from "dayjs";

/**
 * 承認待ち申請カードコンポーネント
 * - タスク待ち申請の情報（申請番号・申請種別・申請者・進行中アクティビティ等）をカード形式で表示する
 * - クリックすると申請詳細画面に遷移する
 * - props: record (承認待ち申請データ)
 */
const ReviewCard: React.FC<{ record: ReviewManagementType }> = ({ record }) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');
  // カードクリック時に申請詳細画面へ遷移する
  const handleCardClick = () => {
    navigate(`/detail/${record.id}`, {
      state: { ...record, from: 'ReviewHistory' },
    });
  };

  return (
    <Card onClick={handleCardClick}
      sx={{
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
          backgroundColor: '#f9f9f9',
        },
        maxHeight: isMobile ? 'none' : 200,
        overflow:'auto',
        whiteSpace: 'nowrap',
      }}
    >
      <Box sx={{display: 'inline-block', minWidth: '100%'}}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', mx: 1, mt: 0.5, gap: 1 }}>
          <Chip
            size="small"
            variant="filled"
            label={`申請番号: ${record.applicationNumber}`}
            color="primary"
            sx={{ maxHeight: '20px' }}
          />
          <Chip
            size="small"
            variant="outlined"
            label={`申請日：${dayjs(record.submittedAt).isValid() ? dayjs(record.submittedAt).format("YYYY/MM/DD") : "未提出"}`}
            color="success"
            sx={{ maxHeight: '20px' }}
          />
        </Box>
        {record.isUrgent && (
          <NotificationImportantRoundedIcon sx={{ color: 'orange', fontSize: '15px' }} />
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mx: 2, my: 1, gap: 0 }}>
        <Box sx={{ display: 'flex', }}>
          <Tooltip
            title={`${record.applicationType}: ${record.applicationFormat}`}
            arrow
          >
            <Typography
              variant="h3"
              sx={{
                display: 'flex',
                maxWidth: 260,
              }}
            >
              <Box sx={{display:'flex',flexDirection:'column'}}>
              <Box component="span" sx={{ fontWeight: 'bold', color: 'black', flexShrink: 0 }}>
                {record.applicationType}
              </Box>
              <Box component="span" sx={{ color: 'gray',
                  }}>
                {record.applicationFormat}
              </Box>
              </Box>
            </Typography>
          </Tooltip>
        </Box>
        <Box>
          <Tooltip title={record.description} arrow>
            <Typography variant="body1" color="textSecondary"
              sx={{
                maxWidth: 260,
              }}
            >
              {record.description}
            </Typography>
          </Tooltip>
        </Box>
        {/*申請者・ユーザプロファイル */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, mt: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>申請者: {record.applicant}</Typography>
          {Array.isArray(record.groups) && record.groups.length > 0 && (
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            所属: {Array.isArray(record.groups) ? record.groups.join('　') : record.groups}</Typography>)}
          {/* 動的に userProfiles を表示 */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {record.userProfiles.map((profile) => (
              <Typography key={profile.key} variant="body2" sx={{ fontWeight: 'bold' }}>
                {profile.key}: {profile.value}
              </Typography>
            ))}
          </Box>
        </Box>
        {/*アクティビティ */}
        {record.applicationStatus === "InProgress" && (
        <Box>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              進行中アクティビティ:
            {record.activities?.slice()
                                .filter((item) => {return item.status === 'InProgress';})
                                ?.filter((item,index) => {return index === 0;})
                                .map((item) =>
                                item.activityName 
                                )}                                          
            </Typography>
        </Box>
        )}
      </Box>
      </Box>
    </Card>
  );
};

export default ReviewCard;